/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-nested-ternary */
import { checkValidDate, isValidZipCode } from '@brands/Utils/validation';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';

import { CaseType, ICase, Status } from '../../../services/cases/types/ICase';
import { UserRoleName } from '../../../services/identity/types/UserProfile';
import { Treatment } from './types';
import { config } from '@brands/config/config';

export const evaluationCodes = [
  'D0120',
  'D0140',
  'D0145',
  'D0150',
  'D0160',
  'D0170',
  'D0171',
  'D0180',
  'D0190',
  'D0191',
];

const checkAllFieldsFilled = (treatment: Treatment): boolean => {
  const { category, tooth, urgency, description } = treatment;
  return !!category?.value && !!tooth?.value && !!urgency?.value && !!description?.trim();
};

const isPharmacyComplete = (pharmacy: any): boolean =>
  !!pharmacy?.pharmacy_name?.trim?.() &&
  !!pharmacy?.pharmacy_street_address?.trim?.() &&
  !!pharmacy?.pharmacy_city?.trim?.() &&
  !!pharmacy?.pharmacy_state?.trim?.() &&
  !!pharmacy?.pharmacy_zip_code?.trim?.() &&
  !!pharmacy?.pharmacy_phone_number?.trim?.();

const pharmacySchema = Yup.object().shape({
  pharmacy_name: Yup.string().trim().required('Pharmacy name is required'),
  pharmacy_street_address: Yup.string().trim().required('Pharmacy street address is required'),
  pharmacy_city: Yup.string().trim().required('Pharmacy city is required'),
  pharmacy_state: Yup.string().trim().required('Pharmacy state is required'),
  pharmacy_zip_code: Yup.string()
    .trim()
    .required('Pharmacy zip code is required')
    .test('is-valid-zip', 'Invalid pharmacy zip code', (value) => (value ? isValidZipCode(value) : true)),
  pharmacy_phone_number: Yup.string()
    .trim()
    .required('Pharmacy phone number is required')
    .test('is-valid-phone', 'Invalid pharmacy phone number', (value) =>
      value ? isValidPhoneNumber(value, { defaultCountry: 'US' }) : true,
    ),
});

export const validationSchema = (
  userInfo: any,
  thisCase: ICase,
  addRequiredField: boolean,
  editingFulfillmentApproval = false,
  journeyPaused = false,
  journeyAtLimit = false,
): any => {
  const isProvider = userInfo.role.name === UserRoleName.Provider;
  const isCareAdvisorLike =
    userInfo.role.name === UserRoleName.CareAdvisor ||
    userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin;

  // care_coordination and oral_health_coaching are care-advisor-led case types
  // with completely different forms — provider validation must not apply to them
  const isStandardCaseType =
    thisCase.type !== CaseType.care_coordination && thisCase.type !== CaseType.oral_health_coaching;

  const isProviderOnStandardCase = isProvider && isStandardCaseType;

  const isCareAdvisorEditing =
    isCareAdvisorLike &&
    (thisCase?.status === Status.AcceptedCareAdvisor || thisCase?.status === Status.InProgressCareAdvisor);

  const isProviderCompleting =
    isProviderOnStandardCase &&
    (thisCase?.status === Status.AcceptedProvider || thisCase?.status === Status.InProgressProvider);

  const isCareAdvisorOnCoachingOrCoordination =
    isCareAdvisorEditing && !isStandardCaseType && config.features.multiStage;

  const followUpEnabled =
    (isProviderOnStandardCase && thisCase.organization?.follow_up_enabled) ||
    (isCareAdvisorOnCoachingOrCoordination && thisCase.organization?.follow_up_enabled);

  return Yup.object({
    is_terms_accepted: isProviderCompleting
      ? Yup.boolean().oneOf([true], 'You must verify that the information is correct')
      : Yup.boolean().nullable(),

    selectedImages: Yup.array().nullable(),

    objective: isProviderOnStandardCase
      ? Yup.object().shape({
          is_media_utilized: Yup.string().nullable().required('Objective: Photos, videos, or radiographs is required'),
          is_intra_oral_utilized: Yup.string().nullable().required('Objective: Intra-oral is required'),
          is_extra_oral_utilized: Yup.string().nullable().required('Objective: Extra-oral is required'),
        })
      : Yup.object().shape({
          is_media_utilized: Yup.string().nullable(),
          is_intra_oral_utilized: Yup.string().nullable(),
          is_extra_oral_utilized: Yup.string().nullable(),
        }),

    cdt:
      isCareAdvisorEditing || !isStandardCaseType
        ? Yup.array().nullable()
        : Yup.array()
            .of(Yup.string())
            .test('one-evaluation-code', 'Please enter one evaluation CDT code', (value) => {
              if (!value || value.length === 0) {
                return false;
              }
              return value.some((code) => evaluationCodes.includes(code || ''));
            })
            .required('At least one CDT code is required'),

    assessment: isProviderOnStandardCase
      ? Yup.string()
          .required('Assessment: Clinical analysis is required')
          .test('not-empty-html', 'Assessment: Clinical analysis is required', (value) => {
            if (!value) return false;

            const stripped = value
              .replace(/<[^>]*>/g, '')
              .replace(/&nbsp;/g, ' ')
              .trim();
            return stripped.length > 0;
          })
      : Yup.string().nullable(),

    plan: isProviderOnStandardCase
      ? Yup.object().shape({
          disposition: Yup.string()
            .required('Plan: Disposition / intervention / recommendations provided is required')
            .test(
              'not-empty-html',
              'Plan: Disposition / intervention / recommendations provided is required',
              (value) => {
                if (!value) return false;

                const stripped = value
                  .replace(/<[^>]*>/g, '')
                  .replace(/&nbsp;/g, ' ')
                  .trim();
                return stripped.length > 0;
              },
            ),
          prescription_in_icore: Yup.mixed().nullable(),
          pharmacy: Yup.mixed().nullable(),
          need_for_oral_health_coaching: Yup.mixed().nullable(),
        })
      : Yup.object().shape({
          disposition: Yup.string().nullable(),
          prescription_in_icore: Yup.mixed().nullable(),
          pharmacy: Yup.mixed().nullable(),
          need_for_oral_health_coaching: Yup.mixed().nullable(),
        }),

    prescriptions:
      addRequiredField && isProviderOnStandardCase
        ? Yup.array()
            .of(
              Yup.object().shape({
                drug: Yup.string().trim().required('Drug is required'),
                sig: Yup.string().trim().required('Sig is required'),
                prescription_date: Yup.string()
                  .trim()
                  .required('Prescription date is required')
                  .test('test-valid-date', 'Enter a valid date in MM/DD/YYYY format', (value) => {
                    if (!value) return false;
                    const [month, day, year] = value.split('/');
                    if (!month || !day || !year) return false;
                    return checkValidDate(month, day, year);
                  }),
                dispense: Yup.string().trim().required('Dispense is required'),
                refills: Yup.string().trim().required('Refills is required'),
                pharmacy: pharmacySchema.required('Pharmacy is required'),
              }),
            )
            .min(1, 'At least one prescription is required')
        : Yup.array().nullable(),

    pharmacies:
      addRequiredField && isProviderOnStandardCase
        ? Yup.mixed().test('pharmacy-of-record-required', 'Pharmacy name is required', function (value) {
            const prescriptions = this.parent?.prescriptions;
            if (Array.isArray(prescriptions) && prescriptions.some((p) => isPharmacyComplete(p?.pharmacy))) {
              return true;
            }
            try {
              pharmacySchema.validateSync(value);
              return true;
            } catch (err) {
              return this.createError({ message: (err as Yup.ValidationError).message });
            }
          })
        : Yup.mixed().nullable(),

    followUp: !followUpEnabled
      ? Yup.object().nullable()
      : config.features.multiStage
      ? Yup.mixed().test(
          'follow-up-decision-required',
          'Please add a follow-up or select that none is needed',
          // A paused or full program disables the follow-up section, so
          // nothing could record the decision.
          () => journeyPaused || journeyAtLimit || thisCase.follow_up_addressed === true,
        )
      : Yup.object()
          .nullable()
          .shape({
            care_coordination:
              thisCase.organization?.care_coordination_enabled || thisCase.organization.share_care_advisor_network
                ? Yup.boolean().required('Care coordination selection is required')
                : Yup.boolean().nullable(),

            oral_health_coaching:
              thisCase.organization?.oral_health_coaching_enabled || thisCase.organization.share_care_advisor_network
                ? Yup.boolean().required('Oral health coaching selection is required')
                : Yup.boolean().nullable(),

            care_coordination_type: Yup.string()
              .when('care_coordination', {
                is: true,
                then: (schema) => schema.required('Care coordination type is required'),
                otherwise: (schema) => schema.nullable(),
              })
              .test('invalid-default-type', 'Care coordination follow-up is required', function (value) {
                const { care_coordination } = this.parent;
                return thisCase.organization?.care_coordination_enabled ||
                  thisCase.organization.share_care_advisor_network
                  ? !(care_coordination === false && value === 'DO-2870')
                  : true;
              }),

            care_coordination_urgency: Yup.string().when('care_coordination', {
              is: true,
              then: (schema) => schema.required('Care coordination urgency is required'),
              otherwise: (schema) => schema.nullable(),
            }),

            care_coordination_reason: Yup.string()
              .trim()
              .when('care_coordination', {
                is: true,
                then: (schema) => schema.required('Care coordination reason is required'),
                otherwise: (schema) => schema.nullable(),
              }),

            care_coordination_other_type: Yup.string().when('care_coordination_type', {
              is: 'other',
              then: (schema) =>
                schema
                  .trim()
                  .required('Care coordination other type is required')
                  .notOneOf([''], 'Care coordination other type cannot be empty'),
              otherwise: (schema) => schema.nullable(),
            }),

            oral_health_coaching_type: Yup.string()
              .when('oral_health_coaching', {
                is: true,
                then: (schema) => schema.required('Oral health coaching type is required'),
                otherwise: (schema) => schema.nullable(),
              })
              .test('invalid-default-type', 'Oral health coaching follow-up is required', function (value) {
                const { oral_health_coaching } = this.parent;
                return thisCase.organization?.oral_health_coaching_enabled ||
                  thisCase.organization.share_care_advisor_network
                  ? !(oral_health_coaching === false && value === 'DO-2870')
                  : true;
              }),

            oral_health_coaching_urgency: Yup.string().when('oral_health_coaching', {
              is: true,
              then: (schema) => schema.required('Oral health coaching urgency is required'),
              otherwise: (schema) => schema.nullable(),
            }),

            oral_health_coaching_reason: Yup.string().when('oral_health_coaching', {
              is: true,
              then: (schema) => schema.trim().required('Oral health coaching reason is required'),
              otherwise: (schema) => schema.nullable(),
            }),

            oral_health_coaching_other_type: Yup.string().when('oral_health_coaching_type', {
              is: 'other',
              then: (schema) =>
                schema
                  .trim()
                  .required('Oral health coaching other type is required')
                  .notOneOf([''], 'Oral health coaching other type cannot be empty'),
              otherwise: (schema) => schema.nullable(),
            }),
          }),

    treatments: isProviderOnStandardCase
      ? Yup.array().test('all-fields-filled', 'Please complete all treatment fields', (treatments = []) => {
          if (treatments.length === 0) {
            return true;
          }

          const hasCompletelyEmptyRow = treatments.some((treatment) => {
            const { category, tooth, urgency, description } = treatment;
            return category?.value === '' && tooth?.value === '' && urgency?.value === '' && description?.trim() === '';
          });

          if (hasCompletelyEmptyRow) {
            return false;
          }

          return treatments.every((treatment) => checkAllFieldsFilled(treatment));
        })
      : Yup.array().of(
          Yup.object({
            category: Yup.object(),
            tooth: Yup.object(),
            urgency: Yup.object(),
            description: Yup.string().trim(),
            toothArea: Yup.object(),
          }),
        ),

    educations:
      userInfo.role.name === UserRoleName.Provider || userInfo.role.name === UserRoleName.CareAdvisor
        ? Yup.array().test('all-fields-filled', 'Please complete all education fields', (educations = []) => {
            if (educations.length === 0) {
              return true;
            }

            return !educations.some((education) => {
              const { url, title } = education;
              return !url?.trim() || !title?.trim();
            });
          })
        : Yup.array().of(
            Yup.object({
              url: Yup.string(),
              title: Yup.string(),
            }),
          ),

    caseNotes: Yup.string().nullable(),

    care_plan:
      thisCase.type === CaseType.oral_health_coaching
        ? Yup.object().shape({
            consultation_summary: Yup.string()
              .trim()
              .required('Consultation summary is required')
              .test('not-empty-html', 'Consultation summary is required', (value) => {
                if (!value) return false;
                const stripped = value.replace(/<[^>]*>/g, '').trim();
                return stripped.length > 0;
              }),
            goals: Yup.array()
              .of(
                Yup.object().shape({
                  goal_text: Yup.string()
                    .required('Goal text must not be empty if a goal exists')
                    .test('goal-has-text', 'Goal text must not be empty if a goal exists', (value, context) =>
                      context?.parent?.educations?.length ? !!value : true,
                    ),
                  educations: Yup.array().of(
                    Yup.object().shape({
                      url: Yup.string().required('Education URL is required if education exists'),
                      title: Yup.string().required('Education title is required if education exists'),
                    }),
                  ),
                }),
              )
              .test('no-empty-goals', 'Remove incomplete or unnecessary goals', (goals) => {
                if (!goals) return true;

                return goals.every(
                  (goal) =>
                    !!goal.goal_text ||
                    (goal.educations?.every((education) => education.url && education.title) ?? true),
                );
              }),
          })
        : Yup.object().nullable(),
    _fulfillment_approval_must_be_saved: config.features.multiStage
      ? Yup.object()
          .nullable()
          .test('must-be-saved', 'Fulfillment approval must be saved before submitting', function () {
            return !editingFulfillmentApproval;
          })
      : Yup.object().nullable(),
  });
};
